import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Check, Circle } from 'lucide-react';
import { clsx } from 'clsx';
import { Popover } from './Popover';
import { useSovereignty, type SovereigntyInfo } from '../../lib/sovereignty';

interface TrustPanelProps {
  info: SovereigntyInfo;
  /** Number of people in the room, when known */
  memberCount?: number;
  /** What the panel describes: a chat, a call, or a guest meeting */
  kind?: 'conversation' | 'call';
}

/**
 * Plain-language answer to "where is this and who can read it?".
 * Only states what is true of the code: TLS in transit, not end-to-end
 * encrypted, AI on the organisation's own Eurion servers. Location, operator
 * and retention come from the admin's /sovereignty.json.
 */
export function TrustPanel({ info, memberCount, kind = 'conversation' }: TrustPanelProps) {
  const { t } = useTranslation();
  const unset = <span className="text-nord-muted">{t('trust.notStated')}</span>;
  const rows: [string, React.ReactNode][] = [
    [t('trust.storedIn'), info.dataLocation
      ? <>{info.dataLocation}{info.operator && <span className="text-nord-muted"> · {info.operator}</span>}</>
      : unset],
    [t('trust.encryption'), (
      <span className="grid gap-0.5">
        <span><Check className="mr-1 inline h-3 w-3 text-aurora-3" aria-hidden="true" />{t('trust.inTransit')}</span>
        <span><Circle className="mr-1 inline h-3 w-3 text-aurora-2" aria-hidden="true" />{t(kind === 'call' ? 'trust.notE2eeCall' : 'trust.notE2ee')}</span>
      </span>
    )],
    [t('trust.whoCanRead'), memberCount != null
      ? t('trust.membersAndAdmins', { count: memberCount })
      : t('trust.participantsAndAdmins')],
    [t('trust.keptFor'), info.retention ?? unset],
    [t('trust.ai'), info.aiModel
      ? t('trust.aiLocalModel', { model: info.aiModel })
      : t('trust.aiLocal')],
  ];
  return (
    <div className="w-[min(24rem,calc(100vw-1rem))] rounded-xl border border-nord-3 bg-nord-1 p-4 text-sm shadow-menu">
      <h2 className="mb-3 text-[15px] font-semibold text-nord-6">
        {t(kind === 'call' ? 'trust.titleCall' : 'trust.title')}
      </h2>
      <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-nord-muted">{k}</dt>
            <dd className="min-w-0 text-nord-4">{v}</dd>
          </div>
        ))}
      </dl>
      {info.privacyUrl && (
        <a href={info.privacyUrl} target="_blank" rel="noreferrer" className="mt-3 inline-block text-nord-8 underline underline-offset-2">
          {t('trust.privacyNotice')}
        </a>
      )}
    </div>
  );
}

interface TrustLineProps {
  memberCount?: number;
  kind?: 'conversation' | 'call';
  /** Extra hint appended after the trust text, e.g. "Shift+Enter for new line" */
  hint?: React.ReactNode;
  className?: string;
}

/** One-line summary ("Encrypted in transit · Stored in Sweden") with a Details popover. */
export function TrustLine({ memberCount, kind, hint, className }: TrustLineProps) {
  const { t } = useTranslation();
  const info = useSovereignty();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const summary = info.dataLocation
    ? t('trust.summaryWithLocation', { location: info.dataLocation })
    : t('trust.summary');
  return (
    <div className={clsx('flex min-w-0 items-center gap-1.5 text-2xs text-nord-muted', className)}>
      <ShieldCheck className="h-3 w-3 flex-none text-aurora-3" aria-hidden="true" />
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="truncate rounded underline decoration-nord-3 underline-offset-2 hover:text-nord-4 hover:decoration-nord-8"
      >
        {summary}
      </button>
      {hint && <span className="hidden truncate sm:inline">· {hint}</span>}
      <Popover open={open} anchorRef={btnRef} onClose={() => setOpen(false)} side="top" align="start" role="dialog" aria-label={t(kind === 'call' ? 'trust.titleCall' : 'trust.title')}>
        <TrustPanel info={info} memberCount={memberCount} kind={kind} />
      </Popover>
    </div>
  );
}
